import React, { memo, useCallback, useContext, useState } from 'react';

import { Button, Select, SelectProps, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import { Label } from '@components/UI';
import { UploadOutlined } from '@ant-design/icons';
import { DefaultOptionType } from 'antd/lib/select';
import SelectByCsvModal from './SelectByCsvModal';

interface ISelectCustomers extends SelectProps {
  showLabel?: boolean;
  isMotBusiness?: boolean;
  allowUpload?: boolean;
  onChange?:
    | ((value: any, option?: DefaultOptionType | DefaultOptionType[]) => void)
    | undefined;
}

const SelectCustomers = ({
  showLabel,
  isMotBusiness,
  allowUpload,
  onChange,
  ...rest
}: ISelectCustomers) => {
  const { t } = useTranslation(['attributes', 'device-actions-modal']);
  const {
    customer: { customersSelectOptions, customersData },
  } = useContext(SessionContext);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const toggleModal = useCallback(
    () => setIsOpenModal(oldOpen => !oldOpen),
    [],
  );

  const handleUpload = useCallback(
    (
      uploadedCustomers: {
        [key: string]: string;
      }[],
    ) => {
      if (!customersData || !onChange || !allowUpload) {
        return;
      }

      const optionsValues = customersData?.map((item: any) => ({
        documentNumber: item.cpfCnpj,
        id: item.id,
      }));

      const availableCustomers = uploadedCustomers
        ?.map(uploadedCustomer => {
          const availableByDocumentNumber = optionsValues.find(
            (opt: any) => opt.documentNumber === uploadedCustomer.cpfCnpj,
          );
          return availableByDocumentNumber
            ? availableByDocumentNumber?.id
            : undefined;
        })
        ?.filter(item => item);
      onChange(availableCustomers);
    },
    [allowUpload, customersData, onChange],
  );

  return (
    <>
      {showLabel && (
        <Label>
          {
            (isMotBusiness
              ? t('attributes:department')
              : t('attributes:customer')) as string
          }
        </Label>
      )}
      <div style={{ display: 'flex', width: '100%' }}>
        <Select
          allowClear
          showSearch
          popupClassName="select-context-switch"
          optionFilterProp="label"
          style={{ width: '100%' }}
          options={customersSelectOptions}
          loading={!customersSelectOptions}
          disabled={!customersSelectOptions}
          filterOption={(input, option) => {
            const verifyString = (str: string) =>
              String(str).toLowerCase().indexOf(input.toLowerCase()) >= 0;
            return verifyString(option?.filterProps);
          }}
          {...rest}
          onChange={onChange}
        />
        {allowUpload && (
          <div style={{ marginLeft: 8 }}>
            <Tooltip
              title={t('device-actions-modal:select-customers.csv-upload')}
            >
              <Button icon={<UploadOutlined />} onClick={toggleModal} />
            </Tooltip>
            <SelectByCsvModal
              isOpen={isOpenModal}
              onCancel={toggleModal}
              onUpload={handleUpload}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default memo(SelectCustomers);
